import React from 'react';
import PropTypes from 'prop-types';
import IntroStartLinks from './IntroStartLinks';

import {
  ELIGIBILITY_URL,
  BDD_INFO_URL,
  DBQ_URL,
  EVIDENCE_NEEDED_URL,
  OMB_CONTROL,
} from '../constants/entryConstants';

// Static new introduction content — CTA logic lives in IntroStartLinks.
// `isBDDForm` adds the BDD-specific content the Veteran needs before starting.
const NewIntroContent = ({ sipProps, loggedIn, isBDDForm = false }) => {
  return (
    <>
      <p>
        Use our online form to fill out and submit your application for VA
        disability compensation if you have a service-connected condition. A
        service-connected condition is an illness or injury that was caused by
        or got worse because of your military service. You can also apply for
        disabilities related to the PACT Act.
      </p>
      <h2 id="main-content">Follow these steps to get started:</h2>
      <va-process-list class="vads-u-padding-y--0">
        <va-process-list-item>
          <h3 className="vads-u-padding-top--0">Check your eligibility</h3>
          <p className="vads-u-margin-top--2">
            Use this form if 1 or more of these scenarios apply to you:
          </p>
          <ul>
            <li>
              You have a service-connected condition caused or worsened by your
              military service.
              <br />
              <va-link
                external
                href={ELIGIBILITY_URL}
                text="Learn about your eligibility for disability compensation"
              />
            </li>
            <li>
              You have a rated service-connected condition that’s gotten worse.
              <br />
              <va-link
                external
                href="/disability/how-to-file-claim/when-to-file/"
                text="Learn about filing a claim for increased disability compensation"
              />
            </li>
            <li>
              You’re a full-time service member separating in the next 90 to 180
              days who qualifies for the Benefits Delivery at Discharge (BDD)
              program.
              <br />
              <va-link
                external
                href={BDD_INFO_URL}
                text="Learn about the BDD program"
              />
            </li>
            <li>
              You qualify for VA healthcare and benefits under the PACT Act.
              <br />
              <va-link
                external
                href="/resources/the-pact-act-and-your-va-benefits"
                text="Learn about the PACT Act"
              />
            </li>
          </ul>
          <p>
            <strong>Note:</strong> If you disagree with our decision on a claim
            you already submitted, you’ll need to request a decision review.
          </p>
          <p>
            <va-link
              external
              href="/decision-reviews/"
              text="Learn more about the decision review process"
            />
          </p>
        </va-process-list-item>
        <va-process-list-item>
          <h3 className="vads-u-padding-top--0">Gather your information</h3>
          {isBDDForm && (
            <>
              <p className="vads-u-margin-top--2">
                When you file a BDD claim online, we’ll ask you to upload this
                required form:{' '}
                <va-link
                  external
                  href={DBQ_URL}
                  rel="noreferrer"
                  target="_blank"
                  text="Separation Health Assessment - Part A Self-Assessment (opens in new tab)"
                />
                . We recommend you download and fill out this form on a desktop
                computer or laptop. Then return to this page to start the
                application process.
              </p>
              <p>
                <strong>Note:</strong> We estimate that it will take you at
                least 30 minutes to complete this form.
              </p>
            </>
          )}
          <p className="vads-u-margin-top--2">
            You may submit any of these types of evidence to support your claim:
          </p>
          <ul>
            <li>Medical records</li>
            <li>Buddy or lay statements (also called written testimonials)</li>
            <li>Discharge or separation papers</li>
            <li>Service treatment records</li>
          </ul>
          <p>
            You can also give us permission to request medical records or test
            results on your behalf.
          </p>
          <p>
            We’ll explain each type of evidence while you fill out your
            application.
          </p>
          <p>
            <va-link
              external
              href={EVIDENCE_NEEDED_URL}
              text="Learn about types of evidence for disability claims"
            />
          </p>
          {isBDDForm && (
            <va-summary-box class="vads-u-margin-bottom--1" uswds>
              <h3 slot="headline" className="vads-u-visibility--screen-reader">
                Note
              </h3>
              <p>
                Please be aware that you’ll need to be available for 45 days
                after you file a BDD claim to complete a VA exam.
              </p>
            </va-summary-box>
          )}
        </va-process-list-item>
        <va-process-list-item>
          <h3 className="vads-u-padding-top--0">Start your claim</h3>
          <p className="vads-u-margin-top--2">
            Next, we’ll ask you a few questions to help you get started.
          </p>
          {isBDDForm && (
            <>
              <p>
                Complete the Benefits Delivery at Discharge form. These are the
                steps you can expect:
              </p>
              <ul>
                <li>Provide your service member information</li>
                <li>Provide your military history</li>
                <li>Describe the conditions you’re submitting a claim for</li>
              </ul>
            </>
          )}
        </va-process-list-item>
      </va-process-list>
      <va-details
        label="What happens after you apply"
        class="vads-u-display--block vads-u-margin-bottom--4"
      >
        <p className="vads-u-margin-top--0">
          We’ll review your claim. We may contact you if we need more evidence.
        </p>
        <p>
          Once we decide your rating, we’ll prepare your decision letter. You’ll
          be able to review and download your letter. We’ll also mail you a
          copy, which should arrive within 10 business days.
        </p>
        <va-link
          external
          href="/disability/after-you-file-claim/"
          text="Learn about the VA claim process after you submit your claim"
        />
      </va-details>
      <IntroStartLinks sipProps={sipProps} loggedIn={loggedIn} />
      <va-omb-info
        class="vads-u-padding-bottom--8"
        res-burden={25}
        omb-number={OMB_CONTROL}
        exp-date="01/31/2029"
      />
    </>
  );
};

NewIntroContent.propTypes = {
  isBDDForm: PropTypes.bool,
  loggedIn: PropTypes.bool,
  sipProps: PropTypes.object,
};

export default NewIntroContent;
